import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Wand2, CheckCircle2, Circle, GripVertical, Plus } from 'lucide-react';
import * as C from '../constants';
import api, { getToken } from '../api';
import AlertToast from './AlertToast';
import { sha256Hex } from '../utils/browserCompat'; // http 站点没有 crypto.subtle，去重要能降级

/**
 * FeatureSettingsModal — 剧本大师 / 文本润色「能力」全局设定（超管）
 *
 * 与"用哪个模型当引擎"解耦：配的是该功能本身（图标 + Skills/Agent），所有模型共用一套；
 * 再从共享语言模型池里勾选这个功能可用哪些模型 —— 勾了哪些，画布对应入口就出现哪些。
 *
 * 存储走 /api/admin/feature-settings/:feature（GET/POST）。
 * 语言模型池 = 右侧「已添加模型」里 model_type='text' 的模型。
 *
 * props: { open, onClose, feature, title, accent, skillsLabel, skillsPlaceholder, intro }
 */
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4100 };
const modalStyle = { width: 680, maxWidth: '94vw', height: '82vh', background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, color: 'var(--text-body)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column' };
const header = { padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 700, fontSize: 14, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', outline: 'none', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' };
const btn = (primary, accent = '#3b82f6') => ({ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (primary ? accent : 'rgba(255,255,255,0.12)'), background: primary ? accent : 'transparent', color: primary ? '#fff' : '#e2e8f0', cursor: 'pointer', fontSize: 13 });

export default function FeatureSettingsModal({ open, onClose, feature, title, accent = '#3b82f6', skillsLabel, skillsPlaceholder, intro, singleSelect = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [triggerIcon, setTriggerIcon] = useState('');
  const [modelIds, setModelIds] = useState([]);           // 勾选的语言模型 DB id
  const [languageModels, setLanguageModels] = useState([]); // 共享池
  const [dragId, setDragId] = useState(null);             // 已选模型拖动排序中的项
  const [iconUploading, setIconUploading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // 🆕 多技能管理状态（仅剧本大师 feature 使用）
  const [skills, setSkills] = useState([]);                // 技能列表
  const [dragSkillId, setDragSkillId] = useState(null);    // 拖动排序中的技能
  const [skillModalOpen, setSkillModalOpen] = useState(false);  // 技能编辑弹窗
  const [editingSkill, setEditingSkill] = useState(null);  // 正在编辑的技能 { id?, name, iconUrl, systemPrompt }

  const showToast = (msg, type = 'success', d = 3000) => { setToastMessage(msg); setToastType(type); setTimeout(() => setToastMessage(''), d); };

  const isScriptMaster = feature === 'script-master';  // 🆕 判断是否剧本大师（需要技能管理）

  useEffect(() => {
    if (!open) return;
    setLoading(true);

    // 🆕 剧本大师：同时加载技能列表
    if (isScriptMaster) {
      Promise.all([
        api.get(`/api/admin/feature-settings/${feature}`),
        api.get('/api/admin/toolbox-skills'),
      ]).then(([settingsData, skillsData]) => {
        setSystemPrompt(settingsData?.systemPrompt || '');
        setTriggerIcon(settingsData?.triggerIcon || '');
        setModelIds(Array.isArray(settingsData?.modelIds) ? settingsData.modelIds : []);
        setLanguageModels(Array.isArray(settingsData?.languageModels) ? settingsData.languageModels : []);
        setSkills(Array.isArray(skillsData?.skills) ? skillsData.skills : []);
      }).catch(e => showToast('❌ 加载失败：' + (e.message || '未知错误'), 'warning'))
        .finally(() => setLoading(false));
    } else {
      api.get(`/api/admin/feature-settings/${feature}`).then(data => {
        setSystemPrompt(data?.systemPrompt || '');
        setTriggerIcon(data?.triggerIcon || '');
        setModelIds(Array.isArray(data?.modelIds) ? data.modelIds : []);
        setLanguageModels(Array.isArray(data?.languageModels) ? data.languageModels : []);
      }).catch(e => showToast('❌ 加载失败：' + (e.message || '未知错误'), 'warning'))
        .finally(() => setLoading(false));
    }
  }, [open, feature, isScriptMaster]);

  if (!open) return null;

  const uploadImage = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      // 拿不到哈希（http 非安全上下文）就跳过去重预检，直接上传
      const hashHex = await sha256Hex(buffer);
      if (hashHex) {
        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
        if (checkResult.exists && checkResult.url) return checkResult.url;
      }
      const formData = new FormData(); formData.append('file', file);
      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: formData }).then(r => r.json());
      if (result.url) return result.url;
      if (result.error) showToast('❌ 上传失败: ' + result.error, 'warning');
    } catch (err) { console.error('上传失败:', err); showToast('❌ 上传失败', 'warning'); }
    return '';
  };

  // 单选模式：点一个就只选这一个；多选模式：切换勾选
  const toggleModel = (id) => setModelIds(prev => singleSelect ? (prev.includes(id) ? [] : [id]) : (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  // 已选模型拖动排序：顺序即画布下拉/上拉框出现顺序
  const reorderTo = (targetId) => {
    setModelIds(prev => {
      if (dragId == null || dragId === targetId) return prev;
      const arr = [...prev];
      const from = arr.indexOf(dragId);
      const to = arr.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      arr.splice(to, 0, arr.splice(from, 1)[0]);
      return arr;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await api.post(`/api/admin/feature-settings/${feature}`, { systemPrompt, triggerIcon, modelIds });
      setSystemPrompt(data?.systemPrompt || '');
      setTriggerIcon(data?.triggerIcon || '');
      setModelIds(Array.isArray(data?.modelIds) ? data.modelIds : []);
      setLanguageModels(Array.isArray(data?.languageModels) ? data.languageModels : []);
      window.dispatchEvent(new Event('modelsUpdated'));
      showToast('✅ 已保存', 'success', 2500);
    } catch (e) { showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning'); }
    finally { setSaving(false); }
  };

  // 🆕 技能管理函数
  const openNewSkill = () => {
    setEditingSkill({ name: '', iconUrl: '', systemPrompt: '' });
    setSkillModalOpen(true);
  };

  const openEditSkill = (skill) => {
    setEditingSkill({ id: skill.id, name: skill.name, iconUrl: skill.icon_url, systemPrompt: skill.system_prompt });
    setSkillModalOpen(true);
  };

  const handleSaveSkill = async () => {
    if (!editingSkill.name.trim()) return showToast('❌ 技能名称不能为空', 'warning');
    if (!editingSkill.systemPrompt.trim()) return showToast('❌ System Prompt 不能为空', 'warning');

    try {
      if (editingSkill.id) {
        // 更新
        await api.patch(`/api/admin/toolbox-skills/${editingSkill.id}`, {
          name: editingSkill.name,
          iconUrl: editingSkill.iconUrl,
          systemPrompt: editingSkill.systemPrompt,
        });
      } else {
        // 新增
        await api.post('/api/admin/toolbox-skills', {
          name: editingSkill.name,
          iconUrl: editingSkill.iconUrl,
          systemPrompt: editingSkill.systemPrompt,
        });
      }
      // 重新加载技能列表
      const skillsData = await api.get('/api/admin/toolbox-skills');
      setSkills(Array.isArray(skillsData?.skills) ? skillsData.skills : []);
      setSkillModalOpen(false);
      setEditingSkill(null);
      showToast('✅ 技能已保存', 'success');
    } catch (e) {
      showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  const handleDeleteSkill = async (id) => {
    if (!window.confirm('确定删除此技能？若有对话正在使用，将无法删除。')) return;
    try {
      await api.delete(`/api/admin/toolbox-skills/${id}`);
      const skillsData = await api.get('/api/admin/toolbox-skills');
      setSkills(Array.isArray(skillsData?.skills) ? skillsData.skills : []);
      showToast('✅ 技能已删除', 'success');
    } catch (e) {
      showToast('❌ 删除失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  const reorderSkillTo = (targetId) => {
    if (dragSkillId == null || dragSkillId === targetId) return;
    const arr = [...skills];
    const from = arr.findIndex(s => s.id === dragSkillId);
    const to = arr.findIndex(s => s.id === targetId);
    if (from < 0 || to < 0) return;
    arr.splice(to, 0, arr.splice(from, 1)[0]);
    setSkills(arr);
    // 立即保存新顺序
    api.put('/api/admin/toolbox-skills/reorder', { order: arr.map(s => s.id) }).catch(e => {
      showToast('❌ 排序失败：' + (e.message || '未知错误'), 'warning');
    });
  };

  const modalContent = (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={header}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent }}><Wand2 size={16} /> {title}</span>
          <X size={18} style={{ cursor: 'pointer', color: 'var(--text-body)' }} onClick={onClose} />
        </div>

        <div className="custom-scrollbar" style={{ flex: 1, padding: 20, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '40px 0' }}>加载中...</div>
          ) : (
            <>
              {intro && (
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6, marginBottom: 18, padding: '8px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>{intro}</div>
              )}

              {/* 触发按钮图标 */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>功能按钮图标（可选）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {triggerIcon ? <img src={triggerIcon} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" /> : <Wand2 size={20} style={{ color: accent, opacity: 0.7 }} />}
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id={`feat-icon-${feature}`}
                    onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; setIconUploading(true); const url = await uploadImage(f); if (url) setTriggerIcon(url); setIconUploading(false); }} />
                  <label htmlFor={`feat-icon-${feature}`} style={{ padding: '8px 16px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: iconUploading ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                    {iconUploading ? '上传中...' : '上传图标'}
                  </label>
                  {triggerIcon && <button onClick={() => setTriggerIcon('')} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
                </div>
              </div>

              {/* Skills / 技能配置 */}
              {isScriptMaster ? (
                // 🆕 剧本大师：多技能管理界面
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={labelStyle}>编剧技能配置（技能共享下方选中的模型）</label>
                    <button onClick={openNewSkill} style={{ padding: '6px 14px', background: accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Plus size={14} /> 新增技能
                    </button>
                  </div>

                  {/* 技能列表（可拖动排序） */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {skills.length === 0 && (
                      <div style={{ fontSize: 12, color: '#64748b', padding: '20px 0', textAlign: 'center' }}>暂无技能，点击右上角「新增技能」开始创建</div>
                    )}
                    {skills.map((skill, i) => (
                      <div key={skill.id}
                        draggable
                        onDragStart={() => setDragSkillId(skill.id)}
                        onDragOver={e => { if (dragSkillId != null) e.preventDefault(); }}
                        onDrop={() => { reorderSkillTo(skill.id); setDragSkillId(null); }}
                        onDragEnd={() => setDragSkillId(null)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                          background: 'rgba(255,255,255,0.05)', borderRadius: 8,
                          border: '1px solid var(--border-subtle)',
                          opacity: dragSkillId === skill.id ? 0.5 : 1,
                          cursor: 'grab',
                        }}>
                        <GripVertical size={16} color="#64748b" style={{ flexShrink: 0 }} />
                        {skill.icon_url ? (
                          <img src={skill.icon_url} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <Wand2 size={20} style={{ color: accent, flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{skill.name}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {skill.system_prompt.slice(0, 60)}{skill.system_prompt.length > 60 ? '...' : ''}
                          </div>
                        </div>
                        {skill.enabled === 0 && <span style={{ fontSize: 10, color: '#f87171', flexShrink: 0 }}>已禁用</span>}
                        <button onClick={() => openEditSkill(skill)} style={{ padding: '6px 12px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>编辑</button>
                        <button onClick={() => handleDeleteSkill(skill.id)} style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>删除</button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // 其他功能：单个 system prompt 输入框（原逻辑）
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>{skillsLabel}</label>
                  <textarea style={{ ...inputStyle, minHeight: 140, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} placeholder={skillsPlaceholder} />
                </div>
              )}

              {/* 选择模型 */}
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>选择模型（{singleSelect ? '单选，节点编辑器点润色即用此模型' : '勾选后在画布对应入口出现；拖动已选项调整顺序 = 画布出现顺序'}）</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8 }}>
                  {languageModels.length === 0 && (
                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6 }}>暂无语言模型。请先在右侧「已添加模型」里添加语言模型（豆包 / Claude / DeepSeek），再回到这里勾选。</div>
                  )}
                  {(() => {
                    const byId = new Map(languageModels.map(m => [m.id, m]));
                    // 已选：按 modelIds 顺序；未选：按原始列表顺序
                    const selected = modelIds.map(id => byId.get(id)).filter(Boolean);
                    const unselected = languageModels.filter(m => !modelIds.includes(m.id));
                    const canDrag = !singleSelect && selected.length > 1;
                    const renderRow = (m, isSelected, draggable) => (
                      <div key={m.id}
                        draggable={draggable}
                        onDragStart={() => draggable && setDragId(m.id)}
                        onDragOver={e => { if (draggable && dragId != null) e.preventDefault(); }}
                        onDrop={() => { if (draggable) { reorderTo(m.id); setDragId(null); } }}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => toggleModel(m.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                          background: isSelected ? 'rgba(96,165,250,0.12)' : 'transparent', border: `1px solid ${isSelected ? 'rgba(96,165,250,0.3)' : 'transparent'}`,
                          opacity: dragId === m.id ? 0.4 : 1 }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                        {draggable
                          ? <GripVertical size={16} color="#64748b" style={{ flexShrink: 0, cursor: 'grab' }} onClick={e => e.stopPropagation()} />
                          : <span style={{ width: 16, flexShrink: 0 }} />}
                        {isSelected ? <CheckCircle2 size={18} color={accent} style={{ flexShrink: 0 }} /> : <Circle size={18} color="#6b7280" style={{ flexShrink: 0 }} />}
                        {m.logoUrl ? <img src={m.logoUrl} alt="" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} /> : <Wand2 size={16} color="#60a5fa" style={{ flexShrink: 0 }} />}
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                        {!m.enabled && <span style={{ fontSize: 10, color: '#f87171', flexShrink: 0 }}>未启用</span>}
                        {m.pricePer1kOutput > 0 && <span style={{ fontSize: 10, color: '#fbbf24', flexShrink: 0 }}>{m.pricePer1kOutput}/千token</span>}
                      </div>
                    );
                    return (
                      <>
                        {selected.map(m => renderRow(m, true, canDrag))}
                        {unselected.map(m => renderRow(m, false, false))}
                      </>
                    );
                  })()}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>未启用的模型即使勾选，画布上也不会出现，需先在「已添加模型」里启用。</div>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btn(false)}>关闭</button>
          <button onClick={handleSave} disabled={saving || loading} style={{ ...btn(true, accent), opacity: (saving || loading) ? 0.6 : 1 }}>{saving ? '保存中...' : '保存设定'}</button>
        </div>
      </div>
      {toastMessage && <AlertToast message={toastMessage} type={toastType} position="center" />}

      {/* 🆕 技能编辑弹窗（嵌套模态框） */}
      {skillModalOpen && editingSkill && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setSkillModalOpen(false); setEditingSkill(null); } }}>
          <div style={{ width: 540, maxWidth: '90vw', maxHeight: '85vh', background: '#1a1a2e', border: '1px solid var(--border-strong)', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 700, fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{editingSkill.id ? '编辑技能' : '新增技能'}</span>
              <X size={18} style={{ cursor: 'pointer', color: 'var(--text-body)' }} onClick={() => { setSkillModalOpen(false); setEditingSkill(null); }} />
            </div>

            <div className="custom-scrollbar" style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
              {/* 技能图标 */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>技能图标（可选）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {editingSkill.iconUrl ? <img src={editingSkill.iconUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" /> : <Wand2 size={20} style={{ color: accent, opacity: 0.7 }} />}
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="skill-icon-upload"
                    onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; setIconUploading(true); const url = await uploadImage(f); if (url) setEditingSkill(s => ({ ...s, iconUrl: url })); setIconUploading(false); }} />
                  <label htmlFor="skill-icon-upload" style={{ padding: '8px 16px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: iconUploading ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                    {iconUploading ? '上传中...' : '上传图标'}
                  </label>
                  {editingSkill.iconUrl && <button onClick={() => setEditingSkill(s => ({ ...s, iconUrl: '' }))} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
                </div>
              </div>

              {/* 技能名称 */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>技能名称 *</label>
                <input
                  autoFocus
                  value={editingSkill.name}
                  onChange={e => setEditingSkill(s => ({ ...s, name: e.target.value }))}
                  placeholder="如：剧本结构大师、角色塑造专家、对白润色师..."
                  style={inputStyle}
                  maxLength={60}
                />
              </div>

              {/* System Prompt */}
              <div style={{ marginBottom: 0 }}>
                <label style={labelStyle}>技能提示词（System Prompt） *</label>
                <textarea
                  value={editingSkill.systemPrompt}
                  onChange={e => setEditingSkill(s => ({ ...s, systemPrompt: e.target.value }))}
                  placeholder="描述这个技能的专业能力和工作方式，如：&#10;你是一位专业的剧本结构分析师，擅长运用三幕式、英雄之旅等经典结构理论，帮助用户搭建完整、合理的剧本框架。"
                  style={{ ...inputStyle, minHeight: 180, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                  maxLength={10000}
                />
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>{editingSkill.systemPrompt.length} / 10000 字</div>
              </div>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setSkillModalOpen(false); setEditingSkill(null); }} style={btn(false)}>取消</button>
              <button onClick={handleSaveSkill} disabled={!editingSkill.name.trim() || !editingSkill.systemPrompt.trim()} style={{ ...btn(true, accent), opacity: (!editingSkill.name.trim() || !editingSkill.systemPrompt.trim()) ? 0.6 : 1 }}>
                {editingSkill.id ? '保存修改' : '创建技能'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
